import {
  isEmpty,
  isNil,
  prop,
  equals,
  keys,
  any,
  not,
  find,
  flip,
  curryN,
  propEq,
} from 'ramda';
import { SubmissionError } from 'redux-form';
import {
  validateEmail,
  validatePhoneNumber,
  validateName,
} from '../../utils/validations';

import messages from './messages';
import { formModes } from './index';
import type { FormMode } from './index';

const petTagRegex = (petTag) => {
  return /^[a-zA-Z0-9 .'-]*$/i.test(petTag);
};
const validateRequired = (property: string, obj: Object) =>
  not(isEmpty(prop(property, obj))) &&
  not(isNil(prop(property, obj))) &&
  !equals('default', prop(property, obj));
const validatePetTag = (intl, tagString) => {
  if (tagString && !petTagRegex(tagString)) {
    return intl.formatMessage(messages.invalidValue);
  }
};
const validateAdults = (
  adults: Array<any>,
  intl: Object,
  mode: FormMode,
  isAffordable: any,
  primaryApplicantId: string,
  additionalPhoneAndEmailFieldsForLeadProfile: boolean = false,
) =>
  adults.map((adult) => {
    const errors = {};
    // Primary/HOH adult: in APPLICATION_HOUSEHOLD use primaryApplicantId; in PROSPECT_CONVERT use isProspect
    const prospectAdult =
      mode === formModes.APPLICATION_HOUSEHOLD
        ? primaryApplicantId === adult.id
        : adult?.isProspect ?? null;
    if (
      mode === formModes.PROSPECT_CONVERT ||
      mode === formModes.APPLICATION_HOUSEHOLD
    ) {
      ['type', 'firstName', 'lastName'].forEach((fieldName) => {
        if (!validateRequired(fieldName, adult))
          errors[fieldName] = intl.formatMessage(messages.required);
      });

      // Validate phone requirement - handle both legacy and new format
      if (additionalPhoneAndEmailFieldsForLeadProfile && adult.phones) {
        if (!adult.phones.length) {
          errors.phones = [
            { phoneNumber: intl.formatMessage(messages.required) },
          ];
        } else {
          // New format: all visible phone fields are required
          adult.phones.forEach((phone, index) => {
            if (!phone || !phone.phoneNumber || !phone.phoneNumber.trim()) {
              if (!errors.phones) errors.phones = [];
              if (!errors.phones[index]) errors.phones[index] = {};
              errors.phones[index].phoneNumber = intl.formatMessage(
                messages.required,
              );
            }
          });
        }
      } else {
        // Legacy format: check phone field
        if (!validateRequired('phone', adult)) {
          errors.phone = intl.formatMessage(messages.required);
        }
      }

      // Validate email requirement - only for main prospect (when converting prospect to applicant)
      if (prospectAdult) {
        if (additionalPhoneAndEmailFieldsForLeadProfile && adult.emails) {
          if (adult.emails.length === 0) {
            errors.emails = [
              { emailAddress: intl.formatMessage(messages.required) },
            ];
          } else {
            // New format: primary email required for prospect
            adult.emails.forEach((email, index) => {
              if (!email?.emailAddress?.trim()) {
                if (!errors.emails) errors.emails = [];
                if (!errors.emails[index]) errors.emails[index] = {};
                errors.emails[index].emailAddress = intl.formatMessage(
                  messages.required,
                );
              }
            });
          }
        } else if (!validateRequired('email', adult)) {
          // Legacy format: email required for main prospect when converting to applicant
          errors.email = intl.formatMessage(messages.required);
        }
      }

      if (!prospectAdult) {
        if (!validateRequired('relationship', adult))
          errors['relationship'] = intl.formatMessage(messages.required);
      }
      if (isAffordable && !validateRequired('affordableRelationship', adult))
        errors['affordableRelationship'] = intl.formatMessage(
          messages.required,
        );
    }

    ['firstName', 'lastName'].forEach((fieldName) => {
      const value = adult[fieldName];
      if (value && !errors[fieldName] && !validateName(value)) {
        errors[fieldName] = intl.formatMessage(messages.invalidValue);
      }
    });

    ['preferredName', 'middleName'].forEach((fieldName) => {
      const value = adult[fieldName];
      if (value && !validateName(value)) {
        errors[fieldName] = intl.formatMessage(messages.invalidValue);
      }
    });

    // Validate legacy phone field - only when feature flag is OFF (user can see/edit this field)
    if (
      !additionalPhoneAndEmailFieldsForLeadProfile &&
      !errors.phone &&
      adult.phone &&
      !validatePhoneNumber(adult.phone)
    ) {
      errors.phone = intl.formatMessage(messages.invalidValue);
    }

    // Validate new phones array - only when feature flag is ON (user can see/edit these fields)
    if (
      additionalPhoneAndEmailFieldsForLeadProfile &&
      adult.phones &&
      Array.isArray(adult.phones)
    ) {
      const phonesErrors = [];
      const phoneValues = [];

      adult.phones.forEach((phone, index) => {
        const phoneErrors = {};

        // Validate phone number format
        if (phone.phoneNumber && !validatePhoneNumber(phone.phoneNumber)) {
          phoneErrors.phoneNumber = intl.formatMessage(messages.invalidValue);
        }

        // Track phone values for duplicate checking
        if (phone.phoneNumber && phone.phoneNumber.trim()) {
          phoneValues.push(phone.phoneNumber.trim());
        }

        phonesErrors[index] = phoneErrors;
      });

      // Check for duplicate phone numbers within this adult
      const uniquePhones = new Set(phoneValues);
      if (phoneValues.length !== uniquePhones.size) {
        // Find which phones are duplicates and mark them
        const seenPhones = new Set();
        adult.phones.forEach((phone, index) => {
          if (phone.phoneNumber && phone.phoneNumber.trim()) {
            const trimmedPhone = phone.phoneNumber.trim();
            if (seenPhones.has(trimmedPhone)) {
              // This is a duplicate
              if (!phonesErrors[index]) phonesErrors[index] = {};
              phonesErrors[index].phoneNumber = intl.formatMessage(
                messages.phoneNumbersMustBeDifferent,
              );
            } else {
              seenPhones.add(trimmedPhone);
            }
          }
        });
      }

      if (
        phonesErrors.some((phoneError) => Object.keys(phoneError).length > 0)
      ) {
        errors.phones = phonesErrors;
      }
    }

    // Validate legacy email field - only when feature flag is OFF (user can see/edit this field)
    if (
      !additionalPhoneAndEmailFieldsForLeadProfile &&
      adult.email &&
      !validateEmail(adult.email)
    ) {
      errors.email = intl.formatMessage(messages.invalidValue);
    }

    // Validate new emails array - only when feature flag is ON (user can see/edit these fields)
    if (
      additionalPhoneAndEmailFieldsForLeadProfile &&
      adult.emails &&
      Array.isArray(adult.emails)
    ) {
      const emailsErrors = [];
      const emailValues = [];

      adult.emails.forEach((email, index) => {
        const emailErrors = {};

        // Validate email format
        if (email.emailAddress && !validateEmail(email.emailAddress)) {
          emailErrors.emailAddress = intl.formatMessage(messages.invalidValue);
        }

        // Track email values for duplicate checking
        if (email.emailAddress && email.emailAddress.trim()) {
          emailValues.push(email.emailAddress.trim());
        }

        emailsErrors[index] = emailErrors;
      });

      // Check for duplicate email addresses within this adult
      const uniqueEmails = new Set(emailValues);
      if (emailValues.length !== uniqueEmails.size) {
        // Find which emails are duplicates and mark them
        const seenEmails = new Set();
        adult.emails.forEach((email, index) => {
          if (email.emailAddress && email.emailAddress.trim()) {
            const trimmedEmail = email.emailAddress.trim();
            if (seenEmails.has(trimmedEmail)) {
              // This is a duplicate
              if (!emailsErrors[index]) emailsErrors[index] = {};
              emailsErrors[index].emailAddress = intl.formatMessage(
                messages.emailAddressesMustBeDifferent,
              );
            } else {
              seenEmails.add(trimmedEmail);
            }
          }
        });
      }

      if (
        emailsErrors.some((emailError) => Object.keys(emailError).length > 0)
      ) {
        errors.emails = emailsErrors;
      }
    }

    return errors;
  });

const validateMinors = (
  minors: Array<any>,
  intl: Object,
  mode: FormMode,
  isAffordable: any,
  agingUpMinorsFlag = false,
) => {
  const minorsErrors = [];

  minors.forEach((minor) => {
    const thisMinorErrors = {};
    const fieldsToValidate = ['firstName', 'lastName', 'relationship'];
    const fieldsToValidateNameAndLast = ['firstName', 'lastName'];

    fieldsToValidate.forEach((fieldName) => {
      if (
        (mode === formModes.PROSPECT_CONVERT ||
          mode === formModes.APPLICATION_HOUSEHOLD) &&
        !validateRequired(fieldName, minor)
      ) {
        thisMinorErrors[fieldName] = intl.formatMessage(messages.required);
      }
      fieldsToValidateNameAndLast.forEach((fieldName) => {
        const value = minor[fieldName];
        if (value && !validateName(value)) {
          thisMinorErrors[fieldName] = intl.formatMessage(
            messages.invalidValue,
          );
        }
      });
    });

    ['middleName', 'preferredName'].forEach((fieldName) => {
      const value = minor[fieldName];
      if (value && !validateName(value)) {
        thisMinorErrors[fieldName] = intl.formatMessage(messages.invalidValue);
      }
    });
    if (isAffordable && !validateRequired('affordableRelationship', minor))
      thisMinorErrors['affordableRelationship'] = intl.formatMessage(
        messages.required,
      );
    minorsErrors.push(thisMinorErrors);
  });
  return minorsErrors;
};

const validatePets = (
  pets: Array<any>,
  petTypeList: Array<any>,
  intl: Object,
  mode: FormMode,
) =>
  pets.map((pet) => {
    const errors = {};
    if (
      any(equals(mode))([
        formModes.PROSPECT_CONVERT,
        formModes.APPLICATION_HOUSEHOLD,
      ])
    ) {
      ['petTypeId', 'breedId'].forEach((fieldName) => {
        if (!validateRequired(fieldName, pet))
          errors[fieldName] = intl.formatMessage(messages.required);
      });
    }

    const selectedPetType =
      find((type) => type.value === pet.petTypeId, petTypeList) || {};

    if (
      !errors.petTypeId &&
      selectedPetType.requiresWeight &&
      !validateRequired('weight', pet)
    ) {
      errors.weight = intl.formatMessage(messages.required);
    }
    if (pet && pet.petTag) {
      const tagErr = validatePetTag(intl, pet.petTag);
      if (tagErr) {
        errors.petTag = tagErr;
      }
    }

    if (
      !errors.weight &&
      !isEmpty(pet.weight) &&
      !isNil(pet.weight) &&
      pet.weight <= 0
    ) {
      errors.weight = intl.formatMessage(messages.invalidValue);
    }

    return errors;
  });

const validate =
  ({
    intl,
    onSubmit,
    petTypeList,
    mode,
    isAffordable,
    primaryApplicantId,
    agingUpMinorsFlag = false,
    booleanOnly = false,
    additionalPhoneAndEmailFieldsForLeadProfile = false,
  }: any) =>
  (values: any) => {
    const { adults, minors, pets } = values;

    const errors = {};
    const adultsErrors = validateAdults(
      adults,
      intl,
      mode,
      isAffordable,
      primaryApplicantId,
      additionalPhoneAndEmailFieldsForLeadProfile,
    );

    if (any((error) => keys(error).length > 0, adultsErrors)) {
      errors.adults = adultsErrors;
    }

    const minorsErrors = validateMinors(
      minors,
      intl,
      mode,
      isAffordable,
      agingUpMinorsFlag,
    );

    if (any((error) => keys(error).length > 0, minorsErrors)) {
      errors.minors = minorsErrors;
    }

    const petsErrors = validatePets(pets, petTypeList, intl, mode);

    if (any((error) => keys(error).length > 0, petsErrors)) {
      errors.pets = petsErrors;
    }

    if (
      mode === formModes.PROSPECT_CONVERT &&
      !validateRequired('unitTypeId', values)
    ) {
      errors.unitTypeId = intl.formatMessage(messages.required);
    }

    const prospectAdult = find(propEq('isProspect', true), adults);

    if (mode === formModes.PROSPECT_HOUSEHOLD && prospectAdult) {
      if (
        not(
          any(flip(curryN(2, validateRequired))(prospectAdult), [
            'firstName',
            'lastName',
            'phone',
            'email',
          ]),
        )
      ) {
        errors._error = intl.formatMessage(messages.prospectValuesRequired);
      }
    }

    if (booleanOnly) {
      return Boolean(keys(errors).length === 0);
    }

    if (keys(errors).length === 0) {
      onSubmit(values);
      return;
    }

    throw new SubmissionError(errors);
  };

export default validate;
